import React, { useState } from 'react'
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
    Typography,
    Button,
    Dialog,
    DialogContent,
    DialogActions,
    Box
} from '@material-ui/core';
import { selectThreadComment, deleteComment } from '../../actions/threadActions';

import Alert from '../../utils/alert';
import ThreadCommentList from './ThreadCommentList';
import ThreadCommentForm from './ThreadCommentForm';

const useStyles = makeStyles((theme) => ({
    commentArea: {
        marginTop: theme.spacing(4),
    },
    commentHeading: {
        fontSize: '1.8rem',
        fontWeight: 700,
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(2),
        borderBottom: `2px solid ${theme.palette.primary.main}`,
        paddingBottom: theme.spacing(1),
    },
    addButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontSize: '1.4rem',
        fontWeight: 600,
        padding: theme.spacing(1.5, 3),
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2),
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            transform: 'translateY(-1px)',
            boxShadow: theme.shadows[4],
        },
        transition: 'all 0.2s ease-in-out',
    },
    dialogContent: {
        padding: theme.spacing(1),
    },
    dialogActions: {
        padding: theme.spacing(2, 3),
        justifyContent: 'flex-start',
        gap: theme.spacing(1),
    },
}));

const ThreadCommentArea = ({ commentList, threadId, currentUserId, threadOwnerId }) => {
    const classes = useStyles();
    const [modalOpen, setModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const dispatch = useDispatch();

    const handleAddComment = () => {
        setEditMode(false);
        setModalOpen(true);
    }

    const handleEditComment = (commentId, comment) => {
        dispatch(selectThreadComment(commentId, comment));
        setEditMode(true);
        setModalOpen(true);
    }

    const handleDeleteComment = (commentId) => {
        Alert.confirm("このコメントを本当に削除しますか？")
            .then((result) => {
                if (result.value) {
                    dispatch(deleteComment(threadId, commentId));
                }
            })
    }

    const handleCloseModal = () => {
        setModalOpen(false);
    }

    const handleUpdate = () => {
        setModalOpen(false);
    }

    return (
        <Box className={classes.commentArea}>
            <Typography variant="h2" component="h2" className={classes.commentHeading}>
                コメント
            </Typography>

            <ThreadCommentList
                commentList={commentList}
                onEdit={handleEditComment}
                onDelete={handleDeleteComment}
                currentUserId={currentUserId}
                threadOwnerId={threadOwnerId}
            />

            {currentUserId && (
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddComment}
                    className={classes.addButton}
                >
                    コメントを追加する
                </Button>
            )}

            <Dialog
                open={modalOpen}
                onClose={handleCloseModal}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    style: {
                        maxHeight: '80vh',
                        overflow: 'hidden',
                        maxWidth: '60rem',
                    }
                }}
            >
                <DialogContent className={classes.dialogContent}>
                    <ThreadCommentForm
                        threadId={threadId}
                        onCancel={handleCloseModal}
                        onUpdate={handleUpdate}
                        editMode={editMode}
                    />
                </DialogContent>
            </Dialog>
        </Box>
    )
}

ThreadCommentArea.propTypes = {
    threadId: PropTypes.string.isRequired,
    commentList: PropTypes.array.isRequired,
    currentUserId: PropTypes.string,
    threadOwnerId: PropTypes.string,
}

export default ThreadCommentArea
